import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import HelpCenterIcon from "@mui/icons-material/HelpCenter";
import TourIcon from "@mui/icons-material/Tour";
import StopIcon from "@mui/icons-material/Stop";
import { Search, X, Menu as MenuIcon } from "lucide-react";
import { 
  Chip, 
  IconButton, 
  Menu, 
  MenuItem, 
  ListItemIcon, 
  ListItemText,
  Divider,
  Badge,
  Tooltip,
  ClickAwayListener,
  Portal
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

import { useAuth } from "../context/AuthContext";
import { useLayout } from "../context/LayoutContext";
import { useTour } from "../context/TourContext";
import "../assets/scss/layout/Header.scss";

const Header = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { 
    selectedDate,
    handleDateChange,
    getCurrentDate,
    headerTitle,
    setHeaderTitle,
    isHeaderVisible,
    setIsHeaderVisible,
    theme,
    toggleNav,
  } = useLayout();
  
  const { 
    isActive: tourIsActive,
    currentTour,
    startTour,
    stopTour,
    startOnboardingSequence
  } = useTour();
  
  // Estados locales
  const [greeting, setGreeting] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [openDateDialog, setOpenDateDialog] = useState(false);
  const [openMobileDateDialog, setOpenMobileDateDialog] = useState(false);
  const [helpAnchorEl, setHelpAnchorEl] = useState(null);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  
  // Referencias para posicionamiento
  const dateChipRef = useRef(null);
  const mobileDateChipRef = useRef(null);
  
  const helpMenuOpen = Boolean(helpAnchorEl);

  // Configurar saludo
  useEffect(() => {
    const getGreeting = () => {
      const hour = new Date().getHours();
      const greetings = [
        { time: [5, 12], text: "Buenos días" },
        { time: [12, 18], text: "Buenas tardes" },
        { time: [18, 24], text: "Buenas noches" },
        { time: [0, 5], text: "Buenas noches" },
      ];

      const selectedGreeting = greetings.find(
        (g) => hour >= g.time[0] && hour < g.time[1]
      );

      return selectedGreeting ? selectedGreeting.text : "Hola";
    };

    setGreeting(getGreeting());
  }, []);

  // Handlers básicos
  const handleSearch = () => {
    if (searchTerm.trim()) {
      navigate(`/global-search?q=${encodeURIComponent(searchTerm.trim())}`);
    }
  };
  
  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };
  
  const handleDateSelection = (newDate) => {
    if (newDate && newDate.isValid()) {
      const formattedDate = newDate.format("YYYY-MM-DD");
      handleDateChange(formattedDate);
    }
    setOpenDateDialog(false);
    setOpenMobileDateDialog(false);
  };

  const handleHelpClick = (event) => setHelpAnchorEl(event.currentTarget);
  const handleHelpClose = () => setHelpAnchorEl(null);

  // Handlers móviles
  const handleMobileSearchToggle = () => {
    setIsMobileSearchOpen(!isMobileSearchOpen);
    if (!isMobileSearchOpen) {
      setSearchTerm("");
    }
  };

  const handleMobileMenuToggle = () => {
    toggleNav();
  };

  // Handlers de tours
  const handleTourSelect = (tourType) => {
    handleHelpClose();
    startTour(tourType);
  };

  const handleStartOnboarding = () => {
    handleHelpClose();
    startOnboardingSequence();
  };

  const handleStopTour = () => {
    handleHelpClose();
    stopTour();
  };

  const handleCloseHeaderTitle = () => {
    setIsHeaderVisible(true);
    setHeaderTitle("");
  };

  // Verificar fecha actual
  const isCurrentDate = useMemo(() => {
    const today = dayjs().format("YYYY-MM-DD");
    return selectedDate === today;
  }, [selectedDate]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <header className={`GM__${theme}-header`}>
        {/* Layout Desktop */}
        <div className={`GM__${theme}-header-desktop`}>
          <div className={`GM__${theme}-header-container`}>
            {/* Saludo/Título */}
            <div className={`GM__${theme}-header-container-greeting`}>
              {isHeaderVisible ? (
                <span>
                  {greeting}, <strong>{user.first_name}</strong>
                </span>
              ) : (
                <div className={`GM__${theme}-header-title-wrapper`}>
                  <span className="route-title">{headerTitle}</span>
                  <IconButton
                    size="small"
                    className={`GM__${theme}-header-close-btn`}
                    onClick={handleCloseHeaderTitle}
                  >
                    <X size={16} />
                  </IconButton>
                </div>
              )}
            </div>

            {/* Búsqueda */}
            <div className={`GM__${theme}-header-container-center`}>
              <div className={`GM__${theme}-search-wrapper`}>
                <input
                  type="text"
                  placeholder="Buscar por placa, operador (cc)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  className={`GM__${theme}-search-input`}
                />
                <IconButton
                  className={`GM__${theme}-search-button`}
                  onClick={handleSearch}
                >
                  <SearchIcon />
                </IconButton>
              </div>
            </div>

            {/* Controles */}
            <div className={`GM__${theme}-header-container-right-section`}>
              {/* Selector de fecha - Desktop */}
              <div className={`GM__${theme}-date-picker-wrapper`}>
                <div ref={dateChipRef}>
                  <Chip
                    icon={<CalendarTodayIcon />}
                    label={getCurrentDate}
                    variant="outlined"
                    onClick={() => setOpenDateDialog(true)}
                    className={`GM__${theme}-chip-pointer ${
                      isCurrentDate ? "chip-current" : "chip-selected"
                    }`}
                  />
                </div>
                
                {/* DatePicker Desktop con Portal */}
                {openDateDialog && (
                  <Portal>
                    <ClickAwayListener onClickAway={() => setOpenDateDialog(false)}>
                      <div 
                        className={`GM__${theme}-date-picker-desktop-portal`}
                        style={{
                          position: 'fixed',
                          top: dateChipRef.current?.getBoundingClientRect().bottom + 8,
                          left: dateChipRef.current?.getBoundingClientRect().left,
                          zIndex: 1400
                        }}
                      >
                        <DatePicker
                          value={dayjs(selectedDate)}
                          onChange={handleDateSelection}
                          views={["year", "month", "day"]}
                          open={true}
                          onClose={() => setOpenDateDialog(false)}
                          slotProps={{
                            textField: {
                              variant: "outlined",
                              size: "small",
                            },
                            popper: {
                              placement: "bottom-start",
                            },
                          }}
                        />
                      </div>
                    </ClickAwayListener>
                  </Portal>
                )}
              </div>

              {/* Sistema de ayuda */}
              <div className={`GM__${theme}-header-actions`}>
                <div className={`GM__${theme}-action-info`}>
                  <span>Conocer la página</span>
                  
                  <Badge 
                    variant="dot" 
                    color="primary" 
                    invisible={!tourIsActive}
                  >
                    <Tooltip title={tourIsActive ? `Tour activo: ${currentTour}` : 'Ayuda y tours'}>
                      <IconButton 
                        className={`GM__${theme}-icon-button`}
                        onClick={handleHelpClick}
                      >
                        <HelpOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Layout Mobile */}
        <div className={`GM__${theme}-header-mobile`}>
          {/* Icono de búsqueda (izquierda) */}
          <IconButton
            className={`GM__${theme}-mobile-search-btn`}
            onClick={handleMobileSearchToggle}
          >
            {isMobileSearchOpen ? <X size={20} /> : <Search size={20} />}
          </IconButton>

          {/* Centro - Fecha o Input de búsqueda */}
          <div className={`GM__${theme}-header-mobile-center`}>
            {isMobileSearchOpen ? (
              <div className={`GM__${theme}-mobile-search-wrapper`}>
                <input
                  type="text"
                  placeholder="Buscar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`GM__${theme}-mobile-search-input`}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch();
                      setIsMobileSearchOpen(false);
                    }
                  }}
                />
              </div>
            ) : (
              <div className={`GM__${theme}-mobile-date-wrapper`}>
                <div ref={mobileDateChipRef}>
                  <Chip
                    icon={<CalendarTodayIcon />}
                    label={dayjs(selectedDate).format("D/MM/YYYY")}
                    variant="outlined"
                    onClick={() => setOpenMobileDateDialog(true)}
                    className={`GM__${theme}-mobile-date-chip ${
                      isCurrentDate ? "chip-current" : "chip-selected"
                    }`}
                  />
                </div>
                
                {/* DatePicker Mobile con Portal */}
                {openMobileDateDialog && (
                  <Portal>
                    <ClickAwayListener onClickAway={() => setOpenMobileDateDialog(false)}>
                      <div 
                        className={`GM__${theme}-date-picker-mobile-portal`}
                        style={{
                          position: 'fixed',
                          top: mobileDateChipRef.current?.getBoundingClientRect().bottom + 8,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          maxWidth: 'calc(100vw - 32px)',
                          width: 'auto',
                          zIndex: 1400
                        }}
                      >
                        <DatePicker
                          value={dayjs(selectedDate)}
                          onChange={handleDateSelection}
                          views={["year", "month", "day"]}
                          open={true}
                          onClose={() => setOpenMobileDateDialog(false)}
                          slotProps={{
                            textField: {
                              variant: "outlined",
                              size: "small",
                            },
                            popper: {
                              placement: "bottom",
                            },
                          }}
                        />
                      </div>
                    </ClickAwayListener>
                  </Portal>
                )}
              </div>
            )}
          </div>

          {/* Icono de menú (derecha) */}
          <IconButton
            className={`GM__${theme}-mobile-menu-btn`}
            onClick={handleMobileMenuToggle}
          >
            <MenuIcon size={20} />
          </IconButton>
        </div>

        {/* Menú de ayuda */}
        <Menu
          anchorEl={helpAnchorEl}
          open={helpMenuOpen}
          onClose={handleHelpClose}
          className={`help-menu GM__${theme}-menu`}
        >
          {tourIsActive && (
            <>
              <MenuItem onClick={handleStopTour}>
                <ListItemIcon>
                  <StopIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Detener tour actual</ListItemText>
              </MenuItem>
              <Divider />
            </>
          )}

          <MenuItem onClick={handleStartOnboarding}>
            <ListItemIcon>
              <TourIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Tour completo de bienvenida</ListItemText>
          </MenuItem>
          
          <MenuItem onClick={() => handleTourSelect('header')}>
            <ListItemIcon>
              <InfoOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Tour del encabezado</ListItemText>
          </MenuItem>
          
          <MenuItem onClick={() => handleTourSelect('leftNav')}>
            <ListItemIcon>
              <MenuBookIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Tour del menú lateral</ListItemText>
          </MenuItem>
          
          <MenuItem onClick={() => handleTourSelect('currentPage')}>
            <ListItemIcon>
              <DashboardCustomizeIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Tour de la página actual</ListItemText>
          </MenuItem>

          <Divider />
          
          <MenuItem onClick={() => handleTourSelect('helpSection')}>
            <ListItemIcon>
              <HelpCenterIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Ir a la sección de ayuda</ListItemText>
          </MenuItem>
        </Menu>
      </header>
    </LocalizationProvider>
  );
};

export default Header;